import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import apiClient from './api';
import moment from 'jalali-moment';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PlaceIcon from '@mui/icons-material/Place';
import EventIcon from '@mui/icons-material/Event';
import FilterListIcon from '@mui/icons-material/FilterList';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import {
    Container, Typography, Box, CircularProgress, Button, Chip, TextField, Snackbar, Alert,
    Select, MenuItem, FormControl, InputLabel, Autocomplete, Collapse,
} from '@mui/material';
import { CONTRACT_LABELS, SHIFT_LABELS, formatToman } from './labels';
import { CITIES, districtSuggestions } from './locations';
import { glass, brand } from './theme';

const EMPTY_FILTERS = { city: '', district: '', service: '', contract_type: '', shift_type: '', q: '' };
const selectSx = { borderRadius: '12px' };
const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: '12px' } };

function RequestCard({ req, amount, onAmount, onBid, submitting }) {
    const band = req.price_estimate;
    const services = req.services.map((s) => s.name);
    return (
        <Box sx={{ ...glass.card, p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5, '&:hover': {} }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                <Typography sx={{ fontWeight: 800, lineHeight: 1.7 }}>
                    {services.join('، ') || 'درخواست مراقبت'}
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ whiteSpace: 'nowrap' }}>
                    {moment(req.created_at).locale('fa').fromNow()}
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: brand.primary }}>
                <PlaceIcon sx={{ fontSize: 18 }} />
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {req.city}{req.district ? ` — ${req.district}` : ''}
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                <Chip size="small" label={CONTRACT_LABELS[req.contract_type] || req.contract_type} />
                <Chip size="small" label={SHIFT_LABELS[req.shift_type] || req.shift_type} />
                <Chip size="small" variant="outlined" icon={<EventIcon />}
                    label={`شروع ${moment(req.start_date).locale('fa').format('D MMMM')}`} />
            </Box>

            {req.notes && (
                <Typography variant="body2" color="text.secondary" sx={{
                    display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.9,
                }}>
                    {req.notes}
                </Typography>
            )}

            {band && (
                <Typography variant="body2" sx={{ bgcolor: brand.accentTint, borderRadius: '10px', px: 1.5, py: 1 }}>
                    بازه‌ی مجاز پیشنهاد: <b>{formatToman(band.provider_bid_min)}</b> تا <b>{formatToman(band.provider_bid_max)}</b>
                </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 'auto' }}>
                <TextField
                    size="small" label="نرخ شما (تومان)" type="number" value={amount || ''}
                    onChange={(e) => onAmount(e.target.value)} sx={{ ...fieldSx, flex: 1, minWidth: 0 }}
                />
                <Button variant="contained" onClick={onBid} disabled={submitting} sx={{ flexShrink: 0 }}>
                    {submitting ? <CircularProgress size={20} color="inherit" /> : 'ارسال پیشنهاد'}
                </Button>
            </Box>
            <Typography variant="caption" color="text.disabled">
                {req.bids_count > 0 ? `${req.bids_count.toLocaleString('fa-IR')} پیشنهاد تا الان` : 'هنوز پیشنهادی ثبت نشده'}
                {' · '}اطلاعات تماس خانواده بعد از قرارداد و پرداخت نمایش داده می‌شود
            </Typography>
        </Box>
    );
}

function AvailableRequests() {
    const [requests, setRequests] = useState([]);
    const [services, setServices] = useState([]);
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [showFilters, setShowFilters] = useState(true);
    const [loading, setLoading] = useState(true);
    const [notApproved, setNotApproved] = useState(false);
    const [amounts, setAmounts] = useState({});
    const [submittingId, setSubmittingId] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        apiClient.get('/api/services/').then((r) => setServices(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    }, []);

    const fetchRequests = useCallback(async (f) => {
        setLoading(true);
        const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== '' && v != null));
        try {
            const response = await apiClient.get('/api/requests/available/', { params });
            setRequests(response.data);
            setNotApproved(false);
        } catch (error) {
            if (error.response?.status === 403) setNotApproved(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const t = setTimeout(() => fetchRequests(filters), 350);
        return () => clearTimeout(t);
    }, [filters, fetchRequests]);

    const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
    const activeCount = Object.values(filters).filter(Boolean).length;

    const handleBid = async (requestId) => {
        const req = requests.find((r) => r.id === requestId);
        const amountToman = amounts[requestId];
        const amountRial = amountToman ? Number(amountToman) * 10 : null;
        const band = req?.price_estimate;
        if (!amountRial) {
            setSnackbar({ open: true, message: 'لطفاً نرخ پیشنهادی خود را وارد کنید.', severity: 'error' });
            return;
        }
        if (band && (amountRial < band.provider_bid_min || amountRial > band.provider_bid_max)) {
            setSnackbar({
                open: true,
                message: `نرخ پیشنهادی باید بین ${formatToman(band.provider_bid_min)} تا ${formatToman(band.provider_bid_max)} باشد.`,
                severity: 'error',
            });
            return;
        }
        setSubmittingId(requestId);
        try {
            await apiClient.post(`/api/requests/${requestId}/bids/`, { amount: amountRial });
            setSnackbar({ open: true, message: `پیشنهاد شما برای درخواست #${requestId} ثبت شد.`, severity: 'success' });
            setRequests((prev) => prev.filter((r) => r.id !== requestId));
        } catch (error) {
            const detail = error.response?.data?.detail || error.response?.data?.non_field_errors?.[0];
            setSnackbar({ open: true, message: detail || 'خطا در ثبت پیشنهاد.', severity: 'error' });
        } finally {
            setSubmittingId(null);
        }
    };

    return (
        <Box sx={{ background: glass.canvasGradient, minHeight: '100%', pb: 6 }}>
            <Container maxWidth="lg" sx={{ pt: 3 }}>
                <Button component={RouterLink} to="/dashboard" startIcon={<ArrowForwardIcon />} size="small" sx={{ mb: 1, color: 'text.secondary' }}>
                    بازگشت به داشبورد
                </Button>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 800, mb: 0.5 }}>
                    درخواست‌های مراقبت
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                    درخواست‌های باز را بر اساس منطقه و نوع خدمت پیدا کنید و نرخ پیشنهادی خودتان را بدهید.
                </Typography>

                {notApproved ? (
                    <Box sx={{ ...glass.card, p: 4, textAlign: 'center', '&:hover': {} }}>
                        <HourglassTopIcon sx={{ fontSize: 48, color: brand.accentDeep }} />
                        <Typography variant="h6" sx={{ fontWeight: 800, mt: 1 }}>پروفایل شما هنوز تأیید نشده است</Typography>
                        <Typography color="text.secondary" sx={{ mt: 1, mb: 2.5 }}>
                            درخواست‌ها فقط برای همیاران تأییدشده نمایش داده می‌شود. پروفایلتان را کامل کنید تا مدیریت بررسی کند.
                        </Typography>
                        <Button component={RouterLink} to="/profile" variant="contained">تکمیل پروفایل</Button>
                    </Box>
                ) : (
                    <>
                        <Box sx={{ ...glass.card, p: 2.5, mb: 3, '&:hover': {} }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: showFilters ? 2 : 0 }}>
                                <Button startIcon={<FilterListIcon />} onClick={() => setShowFilters((v) => !v)} sx={{ fontWeight: 800 }}>
                                    فیلترها{activeCount > 0 ? ` (${activeCount.toLocaleString('fa-IR')})` : ''}
                                </Button>
                                {activeCount > 0 && (
                                    <Button size="small" onClick={() => setFilters(EMPTY_FILTERS)}>پاک‌کردن فیلترها</Button>
                                )}
                            </Box>
                            <Collapse in={showFilters}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                                    <FormControl size="small">
                                        <InputLabel>شهر</InputLabel>
                                        <Select value={filters.city} label="شهر" sx={selectSx}
                                            onChange={(e) => setFilters((p) => ({ ...p, city: e.target.value, district: '' }))}>
                                            <MenuItem value="">همه‌ی شهرها</MenuItem>
                                            {CITIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                    <Autocomplete
                                        freeSolo size="small" options={districtSuggestions(filters.city)}
                                        inputValue={filters.district} onInputChange={(e, v) => setFilter('district', v)}
                                        renderInput={(params) => <TextField {...params} label="منطقه یا محله" sx={fieldSx} />}
                                    />
                                    <FormControl size="small">
                                        <InputLabel>نوع خدمت</InputLabel>
                                        <Select value={filters.service} label="نوع خدمت" sx={selectSx} onChange={(e) => setFilter('service', e.target.value)}>
                                            <MenuItem value="">همه‌ی خدمات</MenuItem>
                                            {services.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                    <FormControl size="small">
                                        <InputLabel>نوع قرارداد</InputLabel>
                                        <Select value={filters.contract_type} label="نوع قرارداد" sx={selectSx} onChange={(e) => setFilter('contract_type', e.target.value)}>
                                            <MenuItem value="">همه</MenuItem>
                                            {Object.entries(CONTRACT_LABELS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                    <FormControl size="small">
                                        <InputLabel>شیفت</InputLabel>
                                        <Select value={filters.shift_type} label="شیفت" sx={selectSx} onChange={(e) => setFilter('shift_type', e.target.value)}>
                                            <MenuItem value="">همه</MenuItem>
                                            {Object.entries(SHIFT_LABELS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                    <TextField size="small" label="جست‌وجو در شرح درخواست" value={filters.q}
                                        onChange={(e) => setFilter('q', e.target.value)} sx={fieldSx} />
                                </Box>
                            </Collapse>
                        </Box>

                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box>
                        ) : requests.length > 0 ? (
                            <>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                    {requests.length.toLocaleString('fa-IR')} درخواست باز
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 2.5 }}>
                                    {requests.map((req) => (
                                        <RequestCard
                                            key={req.id} req={req} amount={amounts[req.id]}
                                            onAmount={(v) => setAmounts((prev) => ({ ...prev, [req.id]: v }))}
                                            onBid={() => handleBid(req.id)} submitting={submittingId === req.id}
                                        />
                                    ))}
                                </Box>
                            </>
                        ) : (
                            <Box sx={{ ...glass.card, p: 4, textAlign: 'center', '&:hover': {} }}>
                                <Typography sx={{ fontWeight: 700 }}>درخواستی با این فیلترها پیدا نشد.</Typography>
                                <Typography variant="body2" color="text.secondary">فیلترها را کم‌تر کنید یا بعداً دوباره سر بزنید.</Typography>
                            </Box>
                        )}
                    </>
                )}
            </Container>

            <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
                <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default AvailableRequests;
